import React, { useState, useEffect } from 'react'
import { uniqueId } from 'lodash'
import IntentDropdownMenu from './IntentDropdownMenu'

interface IProps {
  intent: any
  scrollToIntent: (intentId: number) => void
  inputJson: any[]
  setInputJson: React.Dispatch<React.SetStateAction<any[]>>
  addIntentRef: any
  data: any
}

const IntentCard: React.FC<IProps> = ({
  intent,
  scrollToIntent,
  inputJson,
  setInputJson,
  addIntentRef,
  data
}) => {
  const [newIntentId, setNewIntentId] = useState('')
  const [showSections, setShowSections] = useState(false)
  useEffect(() => {
    let nextIntentId = 1
    while (inputJson.some((obj) => obj.id === nextIntentId)) {
      nextIntentId++
    }
    setNewIntentId(nextIntentId.toString())
  }, [inputJson])

  const renderIntent = (intent: any) => {
    // List Buttons
    if (!!intent.interactive?.type && intent.interactive?.type === 'list') {
      const { header, body, footer, action } = intent.interactive

      return (
        <>
          <h5 className='card-title h4'>
            {!!intent.start ? 'Welcome Intent' : !!intent.end ? 'End Intent' : 'List Buttons'}
          </h5>
          <div className='whatsapp-list-container border rounded p-3'>
            {/* Header */}
            {header?.text && (
              <div className='mb-2'>
                <h5 className='fw-bold text-primary'>{header.text}</h5>
              </div>
            )}

            {/* Body */}
            {body?.text && (
              <div className='mb-2'>
                <p className='mb-0'>{body.text}</p>
              </div>
            )}

            {/* Footer */}
            {footer?.text && (
              <div className='mt-2 text-muted small'>
                <p className='mb-1'>{footer.text}</p>
              </div>
            )}
            <hr className='m-2' style={{ backgroundColor: '#bfc0c5' }} />
            {/* Button Label */}
            {action?.button && (
              <div>
                <button
                  className='btn btn-outline-secondary text-gray-900 w-100'
                  onClick={() => setShowSections(!showSections)}
                >
                  {' '}
                  <i className='bi bi-list-ul text-gray-900 fs-4 fw-bolder'></i>
                  {action.button}
                </button>
              </div>
            )}

            {/* Sections and Rows */}
            {showSections && (
              <>
                {action?.sections?.map((section: any, sectionIndex: number) => (
                  <div key={sectionIndex} className='mb-3 mt-3'>
                    <h6 className='fw-bold text-gray-500'>{section.title}</h6>
                    <ul className='list-group'>
                      {section.rows?.map((row: any, rowIndex: number) => (
                        <li key={rowIndex} className='list-group-item d-flex flex-column'>
                          <span className='fw-semibold'>{row.title}</span>
                          {row.description && (
                            <small className='text-muted'>{row.description}</small>
                          )}
                        </li>
                      ))}
                    </ul>
                  </div>
                ))}
              </>
            )}
          </div>
        </>
      )
    }

    // Buttons
    if (!!intent.interactive?.type && intent.interactive?.type === 'button') {
      const { header, body, footer, action } = intent.interactive

      return (
        <>
          <h5 className='card-title h4'>
            {!!intent.start ? 'Welcome Intent' : !!intent.end ? 'End Intent' : 'Button Options'}
          </h5>
          <div className='whatsapp-button-container border rounded p-3'>
            {/* Header */}
            <div className='d-flex mb-2'>
              {!!intent.intentDescription && (
                <p
                  className='ms-auto text-end text-muted small'
                  dangerouslySetInnerHTML={{ __html: intent.intentDescription }}
                />
              )}
            </div>

            {header?.text && (
              <div className='mb-2'>
                <h5 className='fw-bold text-primary'>{header.text}</h5>
              </div>
            )}

            {/* Body Text */}
            {body?.text && (
              <div className='mb-2'>
                <p className='mb-0'>{body.text}</p>
              </div>
            )}

            {footer?.text && (
              <div className='mt-2 text-muted small'>
                <p className='mb-1'>{footer.text}</p>
              </div>
            )}

            {/* Buttons */}
            <div className='d-flex flex-column gap-2'>
              {action?.buttons?.map((btn: any, index: number) => (
                <button
                  key={btn.reply.id || index}
                  className='btn btn-outline-primary border text-start'
                  // onClick={() => scrollToIntent(btn.reply.trigger)}
                >
                  {btn.reply.title}
                </button>
              ))}
            </div>
          </div>
        </>
      )
    }

    // Text
    if (!!intent.text && intent.type === 'text') {
      return (
        <>
          <div className='d-flex'>
            <h5 className='card-title h4'>
              {!!intent.start ? 'Welcome Intent' : !!intent.end ? 'End Intent' : 'Text'}
            </h5>
            {!!intent.intentDescription && (
              <p
                className='ms-auto text-end  text-mute text-gray-500'
                dangerouslySetInnerHTML={{ __html: intent.intentDescription }}
              />
            )}
          </div>
          {!!intent.text && (
            <p
              className='card-text p-0 mt-3 mb-8'
              dangerouslySetInnerHTML={{ __html: intent.text.body }}
            />
          )}
        </>
      )
    }
    // Action (Webhook)
    if (!!intent.type && intent.type === 'api') {
      const { method, url, body = {}, msgRes = {} } = intent

      return (
        <>
          <h5 className='card-title h4'>
            {!!intent.start ? 'Welcome Intent' : !!intent.end ? 'End Intent' : 'Webhook Action'}
          </h5>

          <div
            className='border rounded p-3 whatsapp-action-container'
            style={{
              backgroundColor: '#f9fafb',
              border: '1px solid #dee2e6',
              borderRadius: '12px',
              boxShadow: '0 2px 6px rgba(0,0,0,0.05)'
            }}
          >
            {/* Intent Description (if exists) */}
            {!!intent.intentDescription && (
              <p
                className='text-muted small mb-3'
                dangerouslySetInnerHTML={{ __html: intent.intentDescription }}
              />
            )}

            {/* Method & URL */}
            <div className='mb-3'>
              <span className='fw-bold text-primary me-2'>Method:</span>
              <span className={`badge ${method === 'POST' ? 'bg-success' : 'bg-info'}`}>
                {method}
              </span>
            </div>
            <div className='mb-3'>
              <span className='fw-bold text-primary me-2'>URL:</span>
              <span className='text-break text-dark fw-semibold'>{url}</span>
            </div>

            <hr />

            {/* Body Section */}
            <div className='mt-3'>
              <h6 className='fw-bold text-secondary'>Body</h6>
              {Object.entries(body).length > 0 ? (
                <ul className='list-group small shadow-sm'>
                  {Object.entries(body).map(([key, value]) => (
                    <li
                      key={key}
                      className='list-group-item d-flex justify-content-between align-items-center'
                    >
                      <span className='fw-semibold text-dark'>{key}</span>
                      <span className='text-muted'>{String(value)}</span>
                    </li>
                  ))}
                </ul>
              ) : (
                <p className='text-muted small fst-italic'>No body parameters defined.</p>
              )}
            </div>

            {/* MsgRes Section */}
            <div className='mt-4'>
              <h6 className='fw-bold text-secondary'>Message Responses</h6>
              {Object.entries(msgRes).length > 0 ? (
                <ul className='list-group small shadow-sm'>
                  {Object.entries(msgRes).map(([status, triggerId]) => (
                    <li
                      key={uniqueId()}
                      className='list-group-item d-flex justify-content-between align-items-center'
                      style={{ cursor: 'pointer' }}
                      // onClick={() => scrollToIntent(Number(triggerId))}
                    >
                      <span className='fw-semibold'>
                        {status === 'default' ? 'Default' : `Status ${status}`}
                      </span>
                      <span className='badge bg-info'>{`Intent ${triggerId}`}</span>
                    </li>
                  ))}
                </ul>
              ) : (
                <p className='text-muted small fst-italic'>No message responses mapped.</p>
              )}
            </div>
          </div>
        </>
      )
    }

    // Text with options
    if (!!intent.type_text && intent.type_text === 'textWithOptions') {
      return (
        <div className=''>
          <h5 className='card-title h4'>
            {!!intent.start
              ? 'Welcome Intent'
              : !!intent.end
              ? 'End Intent'
              : 'Text Message with Options'}
          </h5>

          {/* WhatsApp-like message bubble */}
          <div
            className='px-3 py-2 text-dark'
            style={{ whiteSpace: 'pre-line', lineHeight: '1.8', fontSize: '14px' }}
          >
            {intent.text.body}
          </div>
        </div>
      )
    }

    // Text wit url
    if (!!intent.type && intent.type === 'text_with_url') {
      const body = intent.text?.body || ''
      const urlRegex = /(https?:\/\/[^\s]+)/g

      // Replace URLs in text with clickable <a> tags
      const formattedText = body.split(urlRegex).map((part: any, index: number) => {
        if (urlRegex.test(part)) {
          return (
            <a
              key={index}
              href={part}
              target='_blank'
              rel='noopener noreferrer'
              className='text-primary'
            >
              {part}
            </a>
          )
        }
        return part
      })

      return (
        <div className='mb-3'>
          <h5 className='card-title h4'>
            {!!intent.start ? 'Welcome Intent' : !!intent.end ? 'End Intent' : 'Text with URL'}
          </h5>
          <p className='mb-0'>{formattedText}</p>
        </div>
      )
    }

    // Image
    if (!!intent.type && intent.type === 'image') {
      const imageUrl = intent.image?.link

      return (
        <div className='mb-3'>
          <h5 className='card-title h4'>
            {!!intent.start ? 'Welcome Intent' : !!intent.end ? 'End Intent' : 'Image'}
          </h5>

          {imageUrl ? (
            <div className='text-center'>
              <img
                src={imageUrl}
                alt='Intent'
                className='img-fluid rounded'
                style={{ maxHeight: '300px', objectFit: 'cover' }}
              />
            </div>
          ) : (
            <p className='text-danger'>Image not available</p>
          )}
        </div>
      )
    }
  }

  return (
    <div
      id={`intent-${intent.id}`}
      className='card border border-secondary mb-7'
      style={{
        width: '30rem',
        boxShadow: '0 2px 8px 0 #00000029',
        borderRadius: '15px',
        cursor: 'pointer'
      }}
    >
      <div className='card-body'>
        <div className='d-flex justify-content-between mx-0 px-0 mb-2'>
          <span className='mb-2 me-auto'>
            <span className='ms-1 badge bg-info fs-6'># {intent.id}</span>
          </span>
          <span className='ms-auto text-end'>
            <div className='d-flex justify-content-center flex-shrink-0'>
              <div ref={addIntentRef}>
                <IntentDropdownMenu
                  addIntentDropdownMenuRef={addIntentRef}
                  index={intent.id}
                  intent={intent}
                  inputJson={inputJson}
                  setInputJson={setInputJson}
                  newIntentId={newIntentId}
                  setNewIntentId={setNewIntentId}
                  data={data}
                />
              </div>
            </div>
          </span>
        </div>

        {renderIntent(intent)}

        {/* <div className='d-flex justify-content-between mx-0 px-0 mb-2'>
            {!!intent.userInput && (
              <p className='me-auto text-start mt-2'>
                <span className='ms-2 badge bg-secondary text-info user-select-none'>
                  User Input
                </span>
              </p>
            )}
            {!!intent.trigger && (
              <p
                className='ms-auto text-end mt-2'
                role='button'
                onClick={() => scrollToIntent(intent.trigger)}
              >
                <span className='bg-secondary px-2 py-3 rounded text-dark'>
                  Trigger:
                  <span className='ms-2 badge bg-info'>{intent.trigger}</span>
                </span>
              </p>
            )}
          </div> */}
      </div>
    </div>
  )
}

export default IntentCard
